// 3.4 · one workflow version on its own page (owner, 2026-10-17): a dark palette on the left (Start, the batch's
// endpoints, Logic, the seven Answers; click or drag onto the canvas), the toolbar (version, counts, Save, JSON, Run
// all paths, Execute workflow, Make live), the React Flow canvas, the settings of the selected node or line on the
// right (or the run settings when nothing is selected), the result panel at the bottom; every panel resizable.
// Execute runs each endpoint in the batch's sandbox (the agent, LH-F5). Esc clears the selection, Delete removes.
import { apiMode } from '@/lib/hubApi'
import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ArrowLeft, Braces, ChevronDown, ChevronUp, Clock, Code2, Flag, GitBranch, GitBranchPlus, Moon, Play, Rocket, Route, Save, Sun, Workflow as WorkflowIcon, X } from 'lucide-react'
import { Badge, ConfirmDialog, CopyButton, Modal, Tooltip, applyColourScheme, initials, notify } from '@/kit/ui'
import { TEST_APPLICANTS } from '@/fixtures/flows'
import { batchesOfProduct, compareVersions } from '@/lib/batch'
import { endpointLabel, outcomesOf } from '@/lib/endpoints'
import { ANSWER_KINDS, FLOW_STATUS_LABEL, batchLabel, batchOf, checkFlow, defaultMessage, enumeratePaths, isFlowEditable, newNodeId, pathOk, switchable, type FlowIssue, type FlowPath } from '@/lib/flow'
import { executeFlow, type ExecResult } from '@/lib/flowEngine'
import { executeOnHub } from '@/lib/flowApi'
import { invokeFor } from '@/lib/sandbox'
import { useCurrentUser } from '@/shell/useCurrentUser'
import { useCodeStore } from '@/store/codeStore'
import { useDevStore } from '@/store/devStore'
import { useFlowStore } from '@/store/flowStore'
import { useHubStore } from '@/store/hubStore'
import type { AnswerKind, FlowEdge, FlowNode, Lender, WorkflowVersion } from '@/types'
import { Group, Handle, Pane } from '@/screens/shared/Resizable'
import { workflowUrl } from '../links'
import { useSandbox } from '../useSandbox'
import { DRAG_TYPE, FlowCanvas, NODE_WIDTH } from './FlowCanvas'
import { Inspector } from './Inspector'
import { ApplicantPicker, ReplyPicker, endpointsOf } from './RunInputs'
import { ExecView, PathsView } from './RunViews'
import { FlowStatusBadge } from './WorkflowsSubTab'

const shortName = (name: string) => name.split('.').slice(1).join('.')
const BAD: AnswerKind[] = ['rejected', 'error', 'duplicate']
type Item = { kind: FlowNode['kind']; endpoint?: string; answer?: AnswerKind }

function PaletteItem({ icon: Icon, tint, label, desc, item, onClick, disabled, testid }: { icon: typeof Play; tint: string; label: string; desc: string; item: Item; onClick: () => void; disabled?: boolean; testid?: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      draggable={!disabled}
      onDragStart={(e) => { e.dataTransfer.setData(DRAG_TYPE, JSON.stringify(item)); e.dataTransfer.effectAllowed = 'copy' }}
      className="flex w-full cursor-grab items-center gap-2.5 px-4 py-1.5 text-left transition-colors hover:bg-slate-800 focus:bg-slate-800 focus:outline-none active:cursor-grabbing disabled:cursor-not-allowed disabled:opacity-40"
      title="Click to add, or drag onto the canvas"
    >
      <span className={`flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-md ${tint}`}><Icon size={14} /></span>
      <span className="min-w-0">
        <span className="block truncate text-[12.5px] font-medium text-slate-200">{label}</span>
        <span className="block truncate text-[11px] text-slate-500">{desc}</span>
      </span>
    </button>
  )
}

export function WorkflowEditor({ lender, wfId }: { lender: Lender; wfId: string }) {
  const navigate = useNavigate()
  const { user, can } = useCurrentUser()
  const flows = useFlowStore((s) => s.flows)
  const endpoints = useFlowStore((s) => s.endpoints)
  const batches = useCodeStore((s) => s.batches)
  const apiRecords = useCodeStore((s) => s.apis)
  const users = useHubStore((s) => s.users)
  const liveLender = useHubStore((s) => s.lenders.find((l) => l.slug === lender.slug)) ?? lender
  const { colourScheme, setColourScheme, setCurrentUser } = useDevStore()
  const store = useFlowStore.getState

  const selected = flows.find((f) => f.id === wfId)
  const siblings = useMemo(() => (selected ? flows.filter((f) => f.name === selected.name).sort((a, b) => compareVersions(b.version, a.version)) : []), [flows, selected])
  const tokenRoutes = useMemo(() => apiRecords.filter((a) => a.lenderSlug === lender.slug && a.auth === 'token_source').map((a) => a.endpoint), [apiRecords, lender.slug])
  const oldRoutes = useMemo(() => apiRecords.filter((a) => a.lenderSlug === lender.slug && a.status !== 'active').map((a) => a.endpoint), [apiRecords, lender.slug])

  // the working copy
  const [nodes, setNodes] = useState<FlowNode[]>([])
  const [edges, setEdges] = useState<FlowEdge[]>([])
  const [settings, setSettings] = useState({ waitPerCallS: 8, totalWaitS: 45 })
  const [pin, setPin] = useState<WorkflowVersion['batch']>({ mode: 'live' })
  const [dirty, setDirty] = useState(false)
  const [note, setNote] = useState('')
  const [selection, setSelection] = useState<{ nodeId?: string; edgeId?: string }>({})
  const [applicantId, setApplicantId] = useState('A')
  const [replies, setReplies] = useState<Record<string, string>>({})
  const [result, setResult] = useState<ExecResult | null>(null)
  const [paths, setPaths] = useState<{ list: FlowPath[]; issues: FlowIssue[]; recorded: boolean } | null>(null)
  const [panelOpen, setPanelOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [confirmLive, setConfirmLive] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null)
  const [showJson, setShowJson] = useState(false)

  const working = selected ? { ...selected, nodes, edges, settings, batch: pin } : undefined
  const batch = working ? batchOf(working, batches) : undefined
  // the sandbox of the batch the workflow uses, opened only when Execute needs it
  const sb = useSandbox(liveLender, batch, { auto: false })

  useEffect(() => {
    if (!selected) return
    setNodes(selected.nodes)
    setEdges(selected.edges)
    setSettings(selected.settings)
    setPin(selected.batch)
    setDirty(false)
    setNote('')
    setSelection({})
    setResult(null)
    setPaths(null)
    setReplies({})
    setPanelOpen(false)
  }, [selected?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!dirty) return
    const warn = (e: BeforeUnloadEvent) => e.preventDefault()
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [dirty])

  const editable = !!selected && isFlowEditable(selected) && can('code.edit')

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return
      if (e.key === 'Escape') setSelection({})
      if ((e.key === 'Delete' || e.key === 'Backspace') && editable) {
        if (selection.edgeId) {
          change(nodes, edges.filter((x) => x.id !== selection.edgeId))
          setSelection({})
        } else if (selection.nodeId && nodes.find((n) => n.id === selection.nodeId)?.kind !== 'start') setConfirmDelete(selection.nodeId)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  })

  if (!selected || !working) {
    return (
      <div className="flex h-screen items-center justify-center bg-canvas p-6 text-sm dark:bg-canvas-dark">
        <div className="card text-center">This workflow version does not exist (any more). <Link className="text-primary underline" to={`/lenders/${lender.slug}/api/workflows`}>Back to 3.4 Workflows</Link></div>
      </div>
    )
  }

  const issues = checkFlow(working, batch, tokenRoutes)
  const prodEndpoint = endpoints.find((e) => e.workflowName === selected.name && e.env === 'uat_prod' && e.state !== 'retired')
  // BP-1: this workflow's product's batch (else the lender's whole-lender batch of before)
  const productBatches = batchesOfProduct(batches, lender.slug, selected.productSlug)
  const pinnable = productBatches.filter((b) => b.status !== 'retired').sort((a, b) => compareVersions(b.version, a.version))
  const applicant = TEST_APPLICANTS.find((a) => a.id === applicantId)!.data
  const callable = (batch?.manifest.endpoints ?? []).filter((e) => !e.tokenSource && !tokenRoutes.includes(e.path) && !oldRoutes.includes(e.path))
  const hasStart = nodes.some((n) => n.kind === 'start')

  function change(n: FlowNode[], e: FlowEdge[]) {
    if (!editable) return
    setNodes(n)
    setEdges(e)
    setDirty(true)
    setResult(null)
  }

  const add = (item: Item, at?: { x: number; y: number }) => {
    if (!editable) return
    const right = nodes.reduce((m, n) => Math.max(m, n.pos.x), 0)
    const below = Math.max(0, ...nodes.filter((n) => n.pos.x >= right - 10).map((n) => n.pos.y))
    const pos = at ?? (item.kind === 'start' ? { x: 0, y: 160 } : { x: right + (item.kind === 'answer' ? 0 : NODE_WIDTH + 60), y: item.kind === 'answer' ? below + 100 : 160 })
    const id = newNodeId()
    const k = item.answer ?? 'rejected'
    const node: FlowNode =
      item.kind === 'start' ? { id, kind: 'start', name: 'Start · PI info', pos }
      : item.kind === 'api' ? { id, kind: 'api', name: endpointLabel(item.endpoint ?? '').split('/').pop() || 'endpoint', endpoint: item.endpoint, waitS: null, retries: 0, pos }
      : item.kind === 'decision' ? { id, kind: 'decision', name: 'Decision', condition: { field: 'prev.reply_code', op: 'eq', value: '' }, pos }
      : item.kind === 'wait' ? { id, kind: 'wait', name: 'Wait 5 s', seconds: 5, pos }
      : { id, kind: 'answer', name: `Answer · ${k}`, answer: k, message: defaultMessage(k), pos }
    if (node.kind === 'start' && hasStart) return
    change([...nodes, node], edges)
    setSelection({ nodeId: id })
  }

  const save = () => {
    if (!dirty || !editable) return
    try {
      store().saveFlow(selected.id, { nodes, edges, settings, batch: pin }, note, user.email)
      notify.success('Saved', `${shortName(selected.name)} ${selected.version}: ${nodes.length} nodes, ${edges.length} lines. Run all paths again before Make live.`)
      setDirty(false)
      setNote('')
    } catch (err) {
      notify.error('Save refused', err instanceof Error ? err.message : String(err))
    }
  }

  const execute = async () => {
    if (!batch) return void notify.error('No batch', 'The batch this workflow uses does not exist.')
    setBusy(true)
    try {
      if (apiMode()) {
        // the Hub walks the drawing on the canvas (saved or not) in the batch's sandbox; nothing is recorded
        const r = await executeOnHub(selected.id, { applicant, replies, draft: { nodes, edges, settings, batch: pin } }).catch((err) => { notify.error('Execute refused', err instanceof Error ? err.message : String(err)); return null })
        if (!r) return
        setResult(r)
        setPaths(null)
        setPanelOpen(true)
        return
      }
      const info = sb.status === 'ready' ? sb.info : await sb.open()
      if (!info) return void notify.error('Sandbox not ready', sb.error ?? 'The playground agent is not running: Execute needs it.')
      const r = await executeFlow(working, batch, { applicant, env: { environment: 'uat', ref: String(applicant.ref) }, replies, lenderName: lender.name, fallbackLink: lender.links.web || null, invoke: invokeFor(liveLender, batch) })
      setResult(r)
      setPaths(null)
      setPanelOpen(true)
    } finally {
      setBusy(false)
    }
  }

  const runPaths = () => {
    const list = enumeratePaths(working, batch)
    const recorded = !dirty && isFlowEditable(selected)
    if (recorded) {
      const next = store().recordPaths(selected.id, list, issues, user.email)
      if (next.status === 'paths_passed') notify.success(`All ${list.length} paths end at an Answer`, can('code.publish') ? 'Make live is enabled.' : 'An admin can make it live.')
      else notify.warning(`${list.filter((p) => !pathOk(p)).length + issues.length} problem(s)`, 'See the result below.')
    }
    setPaths({ list, issues, recorded })
    setResult(null)
    setPanelOpen(true)
  }

  const makeLive = () => {
    try {
      const ep = store().makeLive(selected.id, user.email)
      notify.success(`${shortName(selected.name)} ${selected.version} is live`, `Behind ${ep.id}; ${ep.lastSwitch?.from ?? 'the old version'} is previous. Switch back in 3.5 in one click.`)
    } catch (err) {
      notify.error('Make live refused', err instanceof Error ? err.message : String(err))
    }
  }

  const newVersion = () => {
    const f = store().newVersionFrom(selected.id, user.email)
    navigate(workflowUrl(lender.slug, f.id))
  }

  const toggleScheme = () => {
    const next = colourScheme === 'dark' ? 'light' : 'dark'
    applyColourScheme(next)
    setColourScheme(next)
  }

  const trace = result
    ? { nodes: Object.fromEntries(result.steps.map((s) => [s.nodeId, s.port === 'error' ? ('error' as const) : ('ran' as const)])), edges: result.steps.map((s) => s.edge).filter((x): x is string => !!x) }
    : undefined
  const liveBlock = dirty ? 'Save first' : selected.status === 'live' ? 'Already live' : selected.status === 'draft' ? 'Run all paths green first' : selected.status !== 'paths_passed' ? 'Not possible for this version' : !prodEndpoint ? 'No production endpoint runs this workflow yet: create one in 3.5' : (() => { const s = switchable(prodEndpoint, selected, batches); return s.ok ? null : `Not for production: ${s.why}` })()
  const canMake = liveBlock === null && can('code.publish')
  const json = JSON.stringify({ workflow: selected.name, version: selected.version, status: selected.status, lender: lender.slug, product: selected.productSlug, batch: pin, settings, nodes: nodes.map(({ id, kind, name, endpoint, waitS, retries, condition, seconds, answer, message, pos }) => ({ id, kind, name, endpoint, waitS, retries, condition, seconds, answer, message, pos })), edges }, null, 2)
  const sbLabel = sb.status === 'ready' ? 'sandbox ready' : sb.status === 'opening' ? 'starting the sandbox…' : sb.status === 'offline' ? 'agent offline' : sb.status === 'error' ? 'sandbox error' : 'sandbox opens on Execute'

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-slate-50 text-default dark:bg-slate-950" data-testid="workflow-editor">
      <header className="flex h-14 shrink-0 items-center gap-2 border-b border-slate-200 bg-white px-3 dark:border-slate-800 dark:bg-slate-900">
        <Tooltip content={`Back to ${lender.name} · 3.4 Workflows`}>
          <Link to={`/lenders/${lender.slug}/api/workflows`} className="icon-btn" aria-label="Back to 3.4 Workflows" onClick={(e) => { if (dirty && !window.confirm('Leave without saving? The changes on the canvas are lost.')) e.preventDefault() }}><ArrowLeft size={16} /></Link>
        </Tooltip>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <b className="truncate font-mono text-[15px] tracking-tight">{selected.name}</b>
            <select className="rounded-full bg-primary-soft px-2 py-0.5 font-mono text-[11px] font-medium text-primary-text outline-none" aria-label="Version" value={selected.id} onChange={(e) => { if (!dirty || window.confirm('Open another version? The changes on the canvas are lost.')) navigate(workflowUrl(lender.slug, e.target.value)) }}>
              {siblings.map((f) => <option key={f.id} value={f.id}>{f.version} · {FLOW_STATUS_LABEL[f.status]}</option>)}
            </select>
            <FlowStatusBadge status={selected.status} />
            {dirty && <Badge tone="warning">unsaved</Badge>}
          </div>
          <div className="truncate text-[11px] text-muted">
            {nodes.length} nodes · {edges.length} lines · batch {batchLabel(working, batches)} · {issues.length ? <button type="button" className="text-red-600 underline" onClick={runPaths}>{issues.length} check(s) open</button> : 'checks ok'}{selected.madeFrom ? ` · made from ${selected.madeFrom}` : ''} · {sbLabel}
          </div>
        </div>
        <div className="flex-1" />
        {editable && <input className="input !min-h-8 hidden w-44 py-0.5 text-xs lg:block" aria-label="Save note" placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} />}
        {can('code.edit') && <button type="button" className="btn btn-secondary btn-sm" disabled={!dirty || !editable} onClick={save} data-testid="wf-save"><Save size={13} /> Save</button>}
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowJson(true)} data-testid="wf-json"><Braces size={13} /> <span className="hidden md:inline">JSON</span></button>
        {can('code.test') && (
          <>
            <button type="button" className="btn btn-secondary btn-sm" onClick={runPaths} data-testid="wf-paths"><Route size={13} /> <span className="hidden md:inline">Run all paths</span></button>
            <button type="button" className="btn btn-primary btn-sm" disabled={busy || sb.status === 'opening'} onClick={execute} data-testid="wf-execute"><Play size={13} /> {busy ? 'Running…' : 'Execute workflow'}</button>
          </>
        )}
        {can('code.publish') && (
          <Tooltip content={canMake ? `Goes behind ${prodEndpoint?.id ?? 'the production endpoint'}; the live version becomes previous` : liveBlock ?? ''}>
            <button type="button" className="btn btn-accent btn-sm" disabled={!canMake} onClick={() => setConfirmLive(true)} data-testid="wf-live"><Rocket size={13} /> <span className="hidden md:inline">Make live</span></button>
          </Tooltip>
        )}
        {!isFlowEditable(selected) && can('code.edit') && <Tooltip content="New version from this"><button type="button" className="btn btn-ghost btn-sm" onClick={newVersion} data-testid="wf-new-version"><GitBranchPlus size={13} /> <span className="hidden lg:inline">New version</span></button></Tooltip>}
        <button type="button" className="icon-btn" onClick={toggleScheme} aria-label={colourScheme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>{colourScheme === 'dark' ? <Sun size={15} /> : <Moon size={15} />}</button>
        <span className="flex items-center gap-1 border-l border-slate-200 pl-2 dark:border-slate-700">
          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-soft text-2xs font-bold text-primary-text" aria-hidden="true">{initials(user.name)}</span>
          {apiMode() ? <span className="hidden text-2xs sm:block">{user.name} · {user.role}</span> : (
            <select className="hidden bg-transparent text-2xs outline-none sm:block" aria-label="Current user (dev switcher, no login yet)" value={user.id} onChange={(e) => setCurrentUser(e.target.value)}>
              {users.map((u) => <option key={u.id} value={u.id}>{u.name} · {u.role}</option>)}
            </select>
          )}
        </span>
      </header>
      {!isFlowEditable(selected) && (
        <div className="shrink-0 border-b border-primary/30 bg-primary-soft/40 px-4 py-1.5 text-xs dark:bg-primary-soft/10" data-testid="wf-read-only">
          <b>{selected.name} {selected.version}</b> is <b>{FLOW_STATUS_LABEL[selected.status]}</b> and read-only. Execute and Run all paths still work; to change it: <b>New version from this</b>.
        </div>
      )}

      <Group id="workflow-editor" direction="horizontal" className="min-h-0 flex-1">
        <Pane defaultSize={16} minSize={10} maxSize={35} className="max-md:hidden">
          <aside className="flex h-full flex-col bg-slate-900 text-slate-300" aria-label="Palette">
            <div className="border-b border-slate-800 px-4 py-3">
              <div className="flex items-center gap-1.5 text-sm font-semibold tracking-tight text-white"><WorkflowIcon size={15} /> Workflow builder</div>
              <div className="mt-0.5 text-[11px] text-slate-500">{editable ? 'Click a node to add it, or drag it onto the canvas' : 'Read-only: New version from this to change it'}</div>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto py-2">
              <div className="mb-1 mt-1 px-4 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Start</div>
              <PaletteItem icon={Play} tint="bg-teal-950 text-teal-300" label="Start · PI info" desc={hasStart ? 'already on the canvas (one only)' : 'the standard input'} item={{ kind: 'start' }} onClick={() => add({ kind: 'start' })} disabled={!editable || hasStart} />
              <div className="mb-1 mt-3 px-4 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Endpoints · batch {batch?.version ?? '—'}</div>
              {callable.map((e) => (
                <PaletteItem key={e.path} icon={Code2} tint="bg-sky-950 text-sky-300" label={`${e.method} ${e.path}`} desc={outcomesOf(batch?.manifest, batch?.tests ?? [], e.path).join(' · ')} item={{ kind: 'api', endpoint: e.path }} onClick={() => add({ kind: 'api', endpoint: e.path })} disabled={!editable} testid={`palette-${e.path}`} />
              ))}
              {!callable.length && <p className="px-4 text-[11px] text-slate-500">No endpoint in this batch: write one in the playground.</p>}
              {tokenRoutes.length > 0 && <p className="px-4 pt-1 text-[10px] text-slate-500">{tokenRoutes.join(', ')}: token source, called by the Gateway, not here.</p>}
              <div className="mb-1 mt-3 px-4 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Logic</div>
              <PaletteItem icon={GitBranch} tint="bg-violet-950 text-violet-300" label="Decision" desc="field · operator · value → yes / no" item={{ kind: 'decision' }} onClick={() => add({ kind: 'decision' })} disabled={!editable} />
              <PaletteItem icon={Clock} tint="bg-amber-950 text-amber-300" label="Wait" desc="seconds, then on (polling)" item={{ kind: 'wait' }} onClick={() => add({ kind: 'wait' })} disabled={!editable} />
              <div className="mb-1 mt-3 px-4 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Answers</div>
              {ANSWER_KINDS.map((k) => (
                <PaletteItem key={k} icon={Flag} tint={BAD.includes(k) ? 'bg-red-950 text-red-300' : 'bg-emerald-950 text-emerald-300'} label={`Answer · ${k}`} desc={defaultMessage(k).replace('{lender}', lender.name)} item={{ kind: 'answer', answer: k }} onClick={() => add({ kind: 'answer', answer: k })} disabled={!editable} testid={`palette-answer-${k}`} />
              ))}
            </div>
            <div className="border-t border-slate-800 px-4 py-3 text-[11px] text-slate-500">
              Drag from an <span className="text-slate-300">out port ●</span> and drop on a node to join them. <span className="text-slate-300">Esc</span> clears, <span className="text-slate-300">Delete</span> removes.
            </div>
          </aside>
        </Pane>
        <Handle direction="horizontal" />
        <Pane defaultSize={62} minSize={30}>
          <Group id="workflow-editor-main" direction="vertical" className="h-full">
            <Pane defaultSize={panelOpen ? 65 : 96} minSize={25}>
              <FlowCanvas framed={false} height="100%" nodes={nodes} edges={edges} batch={batch} readOnly={!editable} selection={selection} onSelect={setSelection} onChange={change} trace={trace} issueNodes={issues.map((i) => i.nodeId).filter((x): x is string => !!x)} onDrop={(item, pos) => add(item as Item, pos)} />
            </Pane>
            <Handle direction="vertical" />
            <Pane defaultSize={panelOpen ? 35 : 4} minSize={4}>
              <section className="flex h-full flex-col bg-white dark:bg-slate-900" data-testid="wf-result">
                <div className="flex h-8 shrink-0 items-center gap-3 px-3 text-[11px] font-semibold uppercase tracking-wider text-muted">
                  <button type="button" className="flex items-center gap-1 hover:text-default" onClick={() => setPanelOpen(!panelOpen)} aria-expanded={panelOpen}>
                    {panelOpen ? <ChevronDown size={13} /> : <ChevronUp size={13} />} Result
                  </button>
                  <span className="normal-case tracking-normal">{result ? `Execute · answer ${result.answer.status} · ${result.steps.length} steps` : paths ? `Run all paths · ${paths.list.filter(pathOk).length} / ${paths.list.length}` : 'Execute workflow (one run in the sandbox) or Run all paths (every route)'}</span>
                </div>
                {panelOpen && (
                  <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-3">
                    {!result && !paths && <p className="text-xs text-muted">Press <b>Execute workflow</b> (one run on the chosen replies) or <b>Run all paths</b> (every route through every port).</p>}
                    {result && <ExecView result={result} />}
                    {paths && <PathsView {...paths} />}
                  </div>
                )}
              </section>
            </Pane>
          </Group>
        </Pane>
        <Handle direction="horizontal" />
        <Pane defaultSize={22} minSize={14} maxSize={40} className="max-lg:hidden">
          <aside className="flex h-full flex-col bg-white dark:bg-slate-900" aria-label="Settings">
            <div className="flex h-11 shrink-0 items-center justify-between border-b border-slate-200 px-4 dark:border-slate-800">
              <span className="text-[13px] font-semibold">{selection.nodeId ? 'Node settings' : selection.edgeId ? 'Line settings' : 'Input & environment'}</span>
              {(selection.nodeId || selection.edgeId) && <button type="button" className="icon-btn !p-1" aria-label="Close settings" onClick={() => setSelection({})}><X size={15} /></button>}
            </div>
            <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
              {selection.nodeId || selection.edgeId ? (
                <Inspector nodes={nodes} edges={edges} batch={batch} readOnly={!editable} selection={selection} onChange={change} onClose={() => setSelection({})} waitPerCallS={settings.waitPerCallS} tokenRoutes={tokenRoutes} />
              ) : (
                <>
                  <p className="text-2xs text-muted">Click a node or a line for its settings. These are for Execute workflow and Save.</p>
                  <div>
                    <label className="label" htmlFor="wf-env">Environment</label>
                    <select id="wf-env" className="input" defaultValue="uat">
                      <option value="uat">UAT · {lender.env.uatBaseUrl || '(no UAT URL in Lender info)'}</option>
                      <option value="prod" disabled>Production (no Execute from the canvas)</option>
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><label className="label" htmlFor="wf-wait">Wait per call (s)</label><input id="wf-wait" className="input" inputMode="numeric" disabled={!editable} value={settings.waitPerCallS} onChange={(e) => { setSettings({ ...settings, waitPerCallS: Math.max(1, Number(e.target.value) || 1) }); setDirty(true) }} /></div>
                    <div><label className="label" htmlFor="wf-total">Total, then pending (s)</label><input id="wf-total" className="input" inputMode="numeric" disabled={!editable} value={settings.totalWaitS} onChange={(e) => { setSettings({ ...settings, totalWaitS: Math.max(1, Number(e.target.value) || 1) }); setDirty(true) }} /></div>
                  </div>
                  <div>
                    <label className="label" htmlFor="wf-batch">Uses API batch</label>
                    <select id="wf-batch" className="input mono" disabled={!editable} value={pin.mode === 'live' ? 'live' : pin.version} onChange={(e) => { setPin(e.target.value === 'live' ? { mode: 'live' } : { mode: 'pinned', version: e.target.value }); setDirty(true); setResult(null) }}>
                      <option value="live">the live batch ({productBatches.find((b) => b.status === 'live')?.version ?? 'none'}), moves with C1</option>
                      {pinnable.map((b) => <option key={b.id} value={b.version}>pinned {b.version} ({b.status.replace('_', ' ')})</option>)}
                    </select>
                  </div>
                  <ApplicantPicker value={applicantId} onChange={setApplicantId} id="wf-app" />
                  <ReplyPicker batch={batch} routes={endpointsOf(nodes)} value={replies} onChange={setReplies} />
                </>
              )}
            </div>
          </aside>
        </Pane>
      </Group>

      <Modal isOpen={showJson} onClose={() => setShowJson(false)} title="Workflow JSON: what the Gateway runs" size="lg" footer={<div className="flex justify-end"><CopyButton text={json} label="Copy" /></div>}>
        <pre className="max-h-[60vh] overflow-auto whitespace-pre font-mono text-[12px] leading-relaxed" data-testid="wf-json-text">{json}</pre>
      </Modal>
      <ConfirmDialog
        isOpen={confirmDelete !== null}
        onClose={() => setConfirmDelete(null)}
        type="danger"
        title={`Delete ${nodes.find((n) => n.id === confirmDelete)?.name ?? 'node'}?`}
        message="The node and every line to or from it leave the canvas. Save to keep the change."
        confirmText="Delete node"
        onConfirm={() => { if (confirmDelete) { change(nodes.filter((n) => n.id !== confirmDelete), edges.filter((e) => e.from !== confirmDelete && e.to !== confirmDelete)); setSelection({}) } setConfirmDelete(null) }}
      />
      <ConfirmDialog
        isOpen={confirmLive}
        onClose={() => setConfirmLive(false)}
        type="info"
        title={`Make ${shortName(selected.name)} ${selected.version} live?`}
        message={`It goes behind ${prodEndpoint?.id ?? '(no production endpoint)'} now; the web app notices nothing. ${prodEndpoint ? `${shortName(selected.name)} ${prodEndpoint.workflowVersion} becomes previous; switch back in 3.5 in one click.` : ''}`}
        confirmText="Make live"
        onConfirm={() => { makeLive(); setConfirmLive(false) }}
      />
    </div>
  )
}
